import { useEffect, useState, Fragment } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Activity, RefreshCw, Loader2, CheckCircle2, XCircle, PhoneIncoming, PhoneOutgoing,
  Building2, Hash, Wrench, ChevronDown, ChevronRight, Users, AlertTriangle, UserPlus,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const enconOrange = '#ee7203';

/** Small stat tile for the summary row. */
function StatTile({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex-1 min-w-[130px]" data-testid={`8x8-analytics-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-2xl font-bold" style={{ color: accent || '#111827' }}>{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

/** Direction badge — icon + text label so In/Out is never ambiguous at a glance. */
function DirectionBadge({ direction }) {
  const isOutbound = String(direction || '').toLowerCase().startsWith('out');
  return isOutbound ? (
    <span className="flex items-center gap-1 text-blue-600 font-semibold whitespace-nowrap">
      <PhoneOutgoing className="w-3.5 h-3.5" /> Out
    </span>
  ) : (
    <span className="flex items-center gap-1 text-emerald-600 font-semibold whitespace-nowrap">
      <PhoneIncoming className="w-3.5 h-3.5" /> In
    </span>
  );
}

/**
 * Settings → 8x8 Analytics — full visibility into the CDR polling + CRM
 * matching pipeline: raw records pulled from 8x8, how each was processed
 * (branch/extension resolved, caller/callee numbers, CRM match outcome), plus
 * a "Rematch Unmatched" tool to re-run matching on previously-staged calls
 * without waiting for 8x8 to send the same call again.
 */
export const EightXEightAnalyticsSettings = () => {
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [rematching, setRematching] = useState(false);
  const [expandedRaw, setExpandedRaw] = useState(null);

  const load = async (branch = branchFilter) => {
    setLoading(true);
    try {
      const [sRes, rRes, bRes] = await Promise.all([
        api.get(`${API}/8x8/analytics/summary`),
        api.get(`${API}/8x8/analytics/records`, { params: { limit: 100, branch } }),
        api.get(`${API}/8x8/analytics/branches`),
      ]);
      setSummary(sRes.data);
      setRecords(rRes.data?.records || []);
      setBranches(bRes.data?.branches || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load 8x8 Analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBranchFilter = (b) => {
    setBranchFilter(b);
    load(b);
  };

  const handleRematch = async () => {
    setRematching(true);
    try {
      const res = await api.post(`${API}/8x8/analytics/rematch-unmatched`);
      const transferNote = res.data.transfers_resolved > 0 ? `, ${res.data.transfers_resolved} internal-transfer call(s) re-attributed to the right person` : '';
      toast.success(`Checked ${res.data.checked} — ${res.data.now_matched} newly matched to CRM, ${res.data.still_unmatched} still unmatched${transferNote}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh failed');
    } finally {
      setRematching(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="8x8-analytics-settings">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-4 h-4" style={{ color: enconOrange }} /> 8x8 Analytics
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Everything the CDR poller pulled from 8x8, how it was matched (branch/extension/CRM), and a
            tool to refresh anything that didn't match first time — including transferred calls that
            landed on the wrong colleague as an "Unknown number".
          </p>
          {summary?.last_staged_at && (
            <p className="text-[11px] text-gray-400 mt-1" data-testid="8x8-analytics-freshness">
              Data last updated {new Date(summary.last_staged_at).toLocaleString('en-GB')}
              {summary.last_call_start_time && ` · most recent real call time: ${new Date(summary.last_call_start_time.replace(' ', 'T')).toLocaleString('en-GB')}`}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRematch}
            disabled={rematching}
            data-testid="8x8-analytics-rematch-btn"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            {rematching ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wrench className="w-3.5 h-3.5 mr-1" />}
            Refresh Data
          </Button>
          <Button size="sm" onClick={() => load()} disabled={loading} data-testid="8x8-analytics-refresh-btn" className="bg-orange-600 hover:bg-orange-700 text-white">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Reload
          </Button>
        </div>
      </div>

      {loading && !summary ? (
        <div className="text-center text-gray-400 text-sm py-8">Loading…</div>
      ) : (
        <>
          {/* Summary tiles */}
          <div className="flex flex-wrap gap-3" data-testid="8x8-analytics-summary">
            <StatTile label="Total Polled" value={summary?.total_polled ?? 0} />
            <StatTile label="Matched Extension" value={summary?.matched_to_user_extension ?? 0} accent="#15803d" />
            <StatTile label="Unmatched Extension" value={summary?.unmatched_extension ?? 0} accent="#b91c1c" />
            <StatTile label="Matched to CRM" value={summary?.matched_to_crm ?? 0} accent="#1d4ed8" />
            <StatTile label="Unmatched CRM" value={summary?.unmatched_crm ?? 0} accent="#c2410c" />
            <StatTile label="Internal Calls" value={summary?.internal_calls ?? 0} accent="#6b7280" />
            {summary?.errored > 0 && <StatTile label="Errored" value={summary.errored} accent="#dc2626" />}
          </div>

          {/* Per-branch / per-extension volume — capped height + scroll so a long list never
              pushes the raw records table off-screen. */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-gray-200 p-3" data-testid="8x8-analytics-by-branch">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" /> Call Volume by Branch
              </div>
              {(summary?.by_branch || []).length === 0 ? (
                <div className="text-xs text-gray-400">No calls polled yet</div>
              ) : (
                <div className="space-y-1 max-h-[160px] overflow-y-auto pr-1">
                  {summary.by_branch.map((b) => (
                    <div key={b.branch_id} className="flex items-center justify-between text-sm" data-testid={`8x8-analytics-branch-row-${b.branch_id}`}>
                      <span className="text-gray-700">{b.branch_id}</span>
                      <span className="font-bold text-gray-900">{b.calls}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-xl border border-gray-200 p-3" data-testid="8x8-analytics-by-extension">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
                <Hash className="w-3.5 h-3.5" /> Call Volume by Extension
              </div>
              {(summary?.by_extension || []).length === 0 ? (
                <div className="text-xs text-gray-400">No calls polled yet</div>
              ) : (
                <div className="space-y-1 max-h-[160px] overflow-y-auto pr-1">
                  {summary.by_extension.map((e) => (
                    <div key={e.extension} className="flex items-center justify-between text-sm" data-testid={`8x8-analytics-extension-row-${e.extension}`}>
                      <span className="text-gray-700 font-mono">{e.extension}</span>
                      <span className="font-bold text-gray-900">{e.calls}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Raw records table */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-500">Raw Call Records (most recent 100)</div>
              {branches.length > 0 && (
                <div className="flex gap-1 flex-wrap" data-testid="8x8-analytics-branch-tabs">
                  <button
                    onClick={() => handleBranchFilter('')}
                    className={`text-xs px-2.5 py-1 rounded-full border ${branchFilter === '' ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    data-testid="8x8-analytics-branch-tab-all"
                  >
                    All
                  </button>
                  {branches.map((b) => (
                    <button
                      key={b}
                      onClick={() => handleBranchFilter(b)}
                      className={`text-xs px-2.5 py-1 rounded-full border ${branchFilter === b ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                      data-testid={`8x8-analytics-branch-tab-${b}`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {records.length === 0 ? (
              <div className="text-center text-gray-400 text-sm py-8 border border-dashed border-gray-200 rounded-xl">
                No call records polled yet — check Settings &gt; Integrations &gt; 8x8 Diagnostics to confirm polling is running.
              </div>
            ) : (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
                  <table className="w-full text-xs" data-testid="8x8-analytics-records-table">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr className="text-gray-500 uppercase text-[10px]">
                        <th className="text-left px-3 py-2">Time</th>
                        <th className="text-left px-3 py-2">Dir</th>
                        <th className="text-left px-3 py-2">Branch</th>
                        <th className="text-left px-3 py-2">Extension</th>
                        <th className="text-left px-3 py-2">Customer Number</th>
                        <th className="text-left px-3 py-2">CRM Match</th>
                        <th className="text-left px-3 py-2">Assigned To</th>
                        <th className="px-3 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((r) => (
                        <Fragment key={r.call_id}>
                          <tr className="border-t border-gray-100 hover:bg-gray-50" data-testid={`8x8-analytics-record-${r.call_id}`}>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                              {r.start_time ? new Date(r.start_time.replace(' ', 'T')).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                            </td>
                            <td className="px-3 py-2"><DirectionBadge direction={r.direction} /></td>
                            <td className="px-3 py-2 font-semibold text-gray-700" title={r.branch_label || ''}>{r.branch_id || '—'}</td>
                            <td className="px-3 py-2 font-mono text-gray-600" title={r.match_status === 'transferred' ? 'Transferred — showing who actually ended up on the call' : ''}>
                              {r.extension || '—'}
                              {r.match_status === 'transferred' && (
                                <span className="ml-1 text-[9px] uppercase tracking-wide text-blue-500 font-sans font-semibold align-middle" data-testid={`8x8-analytics-transferred-${r.call_id}`}>
                                  transferred
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 font-mono text-gray-600">
                              {r.is_internal_call ? <span className="text-gray-400 italic">internal ext. {r.customer_number}</span> : (r.customer_number || '—')}
                            </td>
                            <td className="px-3 py-2">
                              {r.is_internal_call ? (
                                <span className="flex items-center gap-1 text-gray-500" data-testid={`8x8-analytics-crm-internal-${r.call_id}`}>
                                  <Users className="w-3.5 h-3.5" /> Internal call
                                </span>
                              ) : r.crm_matched ? (
                                <span className="flex items-center gap-1 text-emerald-600 font-semibold" data-testid={`8x8-analytics-crm-matched-${r.call_id}`}>
                                  <CheckCircle2 className="w-3.5 h-3.5" /> {r.account_name || r.contact_name || r.crm_name || 'Matched'}
                                </span>
                              ) : r.match_status === 'unmatched_extension' ? (
                                <span className="flex items-center gap-1 text-red-600 font-semibold" data-testid={`8x8-analytics-crm-unmatched-${r.call_id}`}>
                                  <XCircle className="w-3.5 h-3.5" />
                                  {r.crm_name ? `No Ext — ${r.crm_name}` : 'No Ext — No match'}
                                </span>
                              ) : r.match_status === 'error' ? (
                                <span className="flex items-center gap-1 text-red-700 font-semibold" title={r.error} data-testid={`8x8-analytics-crm-error-${r.call_id}`}>
                                  <AlertTriangle className="w-3.5 h-3.5" /> Error
                                </span>
                              ) : r.eight_x8_name ? (
                                <span className="flex items-center gap-1.5 text-indigo-600 font-semibold" title="Saved as a contact in 8x8 — not yet in CRM" data-testid={`8x8-analytics-crm-8x8only-${r.call_id}`}>
                                  <UserPlus className="w-3.5 h-3.5" />
                                  {r.eight_x8_name}
                                  <span className="text-[9px] uppercase tracking-wide text-indigo-400 font-sans font-semibold">8x8 contact only</span>
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-amber-600 font-semibold" data-testid={`8x8-analytics-crm-unmatched-${r.call_id}`}>
                                  <XCircle className="w-3.5 h-3.5" /> No CRM match
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-700 font-medium" data-testid={`8x8-analytics-notif-bar-${r.call_id}`}>
                              {r.assigned_to_name || r.notification_status || '—'}
                            </td>
                            <td className="px-3 py-2">
                              <button
                                onClick={() => setExpandedRaw(expandedRaw === r.call_id ? null : r.call_id)}
                                className="text-gray-400 hover:text-gray-700"
                                data-testid={`8x8-analytics-expand-raw-${r.call_id}`}
                              >
                                {expandedRaw === r.call_id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </button>
                            </td>
                          </tr>
                          {expandedRaw === r.call_id && (
                            <tr className="bg-gray-50/60" data-testid={`8x8-analytics-raw-row-${r.call_id}`}>
                              <td colSpan={8} className="px-3 py-2">
                                {r.error && <div className="text-[11px] text-red-700 font-mono mb-1">{r.error}</div>}
                                <pre className="text-[10px] text-gray-600 whitespace-pre-wrap break-all">{JSON.stringify(r.raw, null, 2)}</pre>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
